import { Fragment, useCallback, useState } from 'react';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react';
import clsx from 'clsx';
import { CheckIcon, ChevronDownIcon, XIcon } from '@/components/icons';
import {
  StringDropdownItem,
  StringDropdownProps,
} from './StringDropdown.types';
import { Tag } from '@/components/badges/Tag';
import { useTranslation } from '@/hooks/useTranslation';

export default function StringDropdown<
  T extends StringDropdownItem,
  M extends boolean = false,
>({
  availableItems,
  item,
  setItem,
  anchor = { to: 'bottom start', gap: 4 },
  multiple = false as M,
  allowEmpty = false,
  filterable = false,
  itemFormatter = (item: T) => (
    <>
      {item?.icon} {item.label || '-'}
    </>
  ),
  selectedItemFormatter,
  showSelectAllToggle = false,
  disabled = false,
  maxVisibleTags = 3,
  placeholder,
}: StringDropdownProps<T, M>) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');
  const selectAll = useCallback(() => {
    if (multiple && setItem) {
      setItem(availableItems as typeof item);
    }
  }, [multiple, availableItems, setItem]);

  const deselectAll = useCallback(
    (e?: MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (multiple && setItem) {
        setItem([] as any);
      }
    },
    [multiple, setItem],
  );

  const chooseItem = (selectedItem: T | T[]): void => {
    if (disabled) return;

    // Check if any selected item is disabled
    const itemsToCheck = Array.isArray(selectedItem)
      ? selectedItem
      : [selectedItem];
    const hasDisabledItem = itemsToCheck.some((item) => item?.disabled);
    if (hasDisabledItem) return;

    if (multiple) {
      const selectedItems = Array.isArray(selectedItem)
        ? selectedItem
        : [selectedItem];
      setItem(selectedItems as typeof item);
    } else {
      setItem(selectedItem as typeof item);
    }
  };

  const resetItem = () => {
    if (disabled) return;
    if (multiple) {
      setItem([] as any);
    } else {
      setItem(null as typeof item);
    }
  };

  const filteredItems = filterable
    ? availableItems.filter((availableItem) =>
        availableItem.label.toLowerCase().includes(filter.toLowerCase()),
      )
    : availableItems;

  const formatSelectedItem = selectedItemFormatter || itemFormatter;

  const displayLabel = multiple
    ? Array.isArray(item) && item.length > 0
      ? (() => {
          if (maxVisibleTags && item.length > maxVisibleTags) {
            return (
              <>
                {item.slice(0, maxVisibleTags).map((currentItem) => (
                  <Tag color={'gray'} key={currentItem.id}>
                    {itemFormatter(currentItem)}
                  </Tag>
                ))}
                <Tag color="blue">+{item.length - maxVisibleTags}</Tag>
              </>
            );
          }
          return item.map((currentItem, index) => (
            <Tag color={'gray'} key={currentItem.id}>
              {index > 0 && !itemFormatter && ', '}
              {formatSelectedItem(currentItem, true)}
            </Tag>
          ));
        })()
      : (placeholder ?? '-')
    : item
      ? formatSelectedItem(item as T, true)
      : (placeholder ?? '-');

  return (
    <div className="dimension-dropdown-wrapper">
      <Listbox
        as="div"
        value={item}
        onChange={(value) => {
          if (disabled) return;
          const flatValue =
            Array.isArray(value) &&
            value.length === 1 &&
            Array.isArray(value[0])
              ? value[0]
              : value;

          chooseItem(flatValue as T | T[]);
        }}
        multiple={multiple}
        disabled={disabled}
      >
        {({ open }) => (
          <>
            <div className="relative flex items-center">
              <ListboxButton
                className={clsx(
                  'form-input-select',
                  disabled && 'cursor-not-allowed',
                )}
                disabled={disabled}
              >
                <div className="flex items-center w-full mr-4 overflow-x-auto scrollbar-hide max-w-full">
                  <div className="flex items-center gap-2 flex-nowrap">
                    {displayLabel}
                  </div>
                </div>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronDownIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </div>
              </ListboxButton>
              {allowEmpty &&
                (multiple ? (item as T[]).length > 0 : item) &&
                !disabled && (
                  <button
                    className="unstyled"
                    onClick={resetItem}
                    disabled={disabled}
                  >
                    <XIcon
                      className="h-10 w-6 absolute right-8 flex items-center pr-2 text-gray-500 hover:text-gray-700 focus:outline-hidden"
                      aria-hidden="true"
                    />
                  </button>
                )}
            </div>

            <Transition
              show={open && !disabled}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <ListboxOptions
                portal
                anchor={anchor}
                className={clsx(
                  'z-60 w-[var(--button-width)] min-w-fit overflow-y-auto overflow-x-hidden rounded-md bg-white text-base shadow-lg',
                  'ring-1 ring-black/5 focus:outline-hidden sm:text-sm max-h-60 py-1',
                  disabled && 'pointer-events-none',
                )}
              >
                {filteredItems.map((currentItem) => (
                  <ListboxOption
                    key={currentItem.id}
                    className={clsx(
                      'listbox-option group relative select-none py-2 pl-3 pr-9 text-gray-900 overflow-hidden',
                      currentItem.disabled
                        ? 'cursor-not-allowed text-gray-400 bg-gray-50'
                        : 'cursor-default data-[selected]:active data-focus:text-white data-focus:bg-blue-600',
                    )}
                    value={currentItem}
                    disabled={currentItem.disabled}
                  >
                    {({ selected }) => (
                      <div className="flex items-center gap-1 min-w-0">
                        <div
                          className={clsx(
                            selected ? 'font-semibold' : 'font-normal',
                            currentItem.disabled ? 'text-gray-400' : '',
                            'ml-1 flex items-center gap-1 min-w-0 flex-1 truncate',
                          )}
                          title={currentItem.label}
                        >
                          {itemFormatter(currentItem, selected)}
                        </div>

                        {selected && !currentItem.disabled && (
                          <div
                            className={clsx(
                              'icon absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600',
                              'group-data-[focus]:text-white',
                            )}
                          >
                            <CheckIcon
                              className="h-5 w-5 shrink-0"
                              aria-hidden="true"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Transition>
          </>
        )}
      </Listbox>

      {multiple && showSelectAllToggle && (
        <div className="flex gap-2 my-2 text-sm">
          <span
            onClick={() => {
              selectAll();
            }}
            className="text-purple-500 hover:underline focus:outline-none cursor-pointer"
          >
            {t('global:selectAll')}
          </span>
          <span>|</span>
          <span
            onClick={() => {
              deselectAll();
            }}
            className="text-purple-500 hover:underline focus:outline-none cursor-pointer"
          >
            {t('global:selectNone')}
          </span>
        </div>
      )}
    </div>
  );
}

export const enumKeysToDropdownItems = (
  enumObject: object,
  labelFunction: (key: string) => string,
) => {
  return Object.keys(enumObject).map((key) => ({
    id: key,
    label: labelFunction(key),
  }));
};
export const enumValuesToDropdownItems = (
  enumObject: object,
  labelFunction: (key: string) => string,
) => {
  return Object.values(enumObject).map((key) => ({
    id: key,
    label: labelFunction(key),
  }));
};
